import {
  useCallback, useContext, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { sendAnnouncement as sendAnnouncementHandler } from 'network/twilioRequests';
import {
  selectCurrentSelectedChat,
  TwilioMessageAttributes,
  selectMetadataById,
  selectUserById,
  PrivateConversationMetadata,
  ConversationType,
  setChatAlert,
  ChatAlert,
} from 'models/twilio';
import useMessageTracking from 'components/twilio/hooks/useMessageTracking';
import metadataEquality from '../utils/metadataEquality';
import useCurrentAttendee from './useCurrentAttendee';
import ConverastionContext from '../ConversationContext';
import TRANSLATIONS from '../translations';

const ANNOUNCEMENT_ALERTS = {
  PROCESSING: {
    message: TRANSLATIONS.COMMON.ANNOUNCEMENT_PROCESSING[1],
    translationKey: TRANSLATIONS.COMMON.ANNOUNCEMENT_PROCESSING[0],
    severity: 'info',
  } as ChatAlert,
  SUCCESS: {
    message: TRANSLATIONS.COMMON.ANNOUNCEMENT_SUCCESS[1],
    translationKey: TRANSLATIONS.COMMON.ANNOUNCEMENT_SUCCESS[0],
    severity: 'success',
  } as ChatAlert,
  FAIL: {
    message: TRANSLATIONS.ERRORS.ANNOUNCEMENT_FAIL[1],
    translationKey: TRANSLATIONS.ERRORS.ANNOUNCEMENT_FAIL[0],
    severity: 'error',
  } as ChatAlert,
} as const;

const useTwilioMessageSend = () => {
  const dispatch = useDispatch();
  const { uuid } = useCurrentAttendee();
  const trackSendAction = useMessageTracking();
  const currentSelectedChat = useSelector(selectCurrentSelectedChat);
  const { getConversationById } = useContext(ConverastionContext);
  const conversation = getConversationById(currentSelectedChat);
  const metadata = useSelector(selectMetadataById(currentSelectedChat), metadataEquality({
    otherUserId: (prevId, nextId) => prevId === nextId,
    conversationName: (prevName, nextName) => prevName === nextName,
    id: (prevId, nextId) => prevId === nextId,
    conversationType: (prevType, nextType) => prevType === nextType,
  }));
  const otherUser = useSelector(selectUserById((metadata as PrivateConversationMetadata)?.otherUserId));

  const sendMessage = useCallback(async (message: string, messageAttributes: TwilioMessageAttributes = {}) => {
    if (!conversation || !metadata) return;
    trackSendAction(metadata, otherUser, messageAttributes);
    await conversation.sendMessage(message, messageAttributes);
    await conversation.setAllMessagesRead();
  }, [conversation, trackSendAction, metadata, otherUser]);

  const sendAnnouncement = useCallback(async (message: string, messageAttributes: TwilioMessageAttributes = {}) => {
    dispatch(setChatAlert(ANNOUNCEMENT_ALERTS.PROCESSING));
    const success = await sendAnnouncementHandler(uuid, message, messageAttributes);
    if (success) dispatch(setChatAlert(ANNOUNCEMENT_ALERTS.SUCCESS));
    else dispatch(setChatAlert(ANNOUNCEMENT_ALERTS.FAIL));
  }, [dispatch, uuid]);

  return useMemo(() => ({
    sendMessage: metadata?.conversationType === ConversationType.ANNOUNCEMENT ? sendAnnouncement : sendMessage,
  }), [metadata?.conversationType, sendAnnouncement, sendMessage]);
};

export default useTwilioMessageSend;
